import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Notification from "../../components/Notification.js";
import { API_URL } from "../../config.js";
import { allowedToView } from "../../utils/PermissionUtils.js";
import { formatTimestamp, formatToDayMonthYear, formatCurrency } from "../../utils/TextUtils.js";

function MilkSales() {
    const [milkSales, setMilkSales] = useState([]);
    const [totalLitresSold, setTotalLitresSold] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(null);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [selectedMilkSale, setSelectedMilkSale] = useState(null);
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filterDate, setFilterDate] = useState(""); // Filter by specific date
    const [filterMonth, setFilterMonth] = useState(""); // Filter by month and year
    const [filterRoute, setFilterRoute] = useState(""); // Filter by route
    const [filterShift, setFilterShift] = useState(""); // Filter by shift
    const [routes, setRoutes] = useState([]);
    const perPage = 10;
    const token = localStorage.getItem("access_token");
    const userRole = localStorage.getItem("userRole");
    const [summaryLoading, setSummaryLoading] = useState(false);

    // Define shifts array
    const shifts = [
        { value: "", label: "All Shifts" },
        { value: "Morning", label: "Morning" },
        { value: "Evening", label: "Evening" }
    ];

    const canEdit = (userRole) => {
        return (allowedToView(userRole, ['admin', 'superadmin', 'collection_center_manager']));
    };

    const isAdmin = allowedToView(userRole, ['admin']);
    const isSuperAdmin = allowedToView(userRole, ['superadmin']);
    const navigate = useNavigate();

    // Fetch routes from API
    const fetchRoutes = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/collection-routes`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setRoutes(data || []);
            }
        } catch (error) {
            console.error("Error fetching routes:", error);
        }
    }, [token]);

    // Fetch milk sales
    const fetchMilkSales = useCallback(async () => {
        try {
            setLoading(true);
            let url = `${API_URL}/milk-sales?page=${currentPage}&limit=${perPage}`;
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (filterDate) params.append('date', filterDate);
            if (filterMonth) params.append('month', filterMonth);
            if (filterRoute) params.append('route_id', filterRoute);
            if (filterShift) params.append('shift', filterShift);

            const response = await fetch(`${url}&${params.toString()}`, {
                headers: { "Authorization": `Bearer ${token}` },
            });

            if (response.ok) {
                const data = await response.json();
                setMilkSales(data.data.data || []);
                setTotalPages(data.data.last_page || 1);
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                throw new Error("Failed to fetch milk sales");
            }
        } catch (error) {
            setError("An error occurred while fetching milk sales.");
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchTerm, filterDate, filterMonth, filterRoute, filterShift, navigate, token, perPage]);


    const fetchSummary = useCallback(async () => {
        try {
            setSummaryLoading(true);
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (filterDate) params.append('date', filterDate);
            if (filterMonth) params.append('month', filterMonth);
            if (filterRoute) params.append('route_id', filterRoute);
            if (filterShift) params.append('shift', filterShift);

            const response = await fetch(`${API_URL}/milk-sales/summary?${params.toString()}`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setTotalLitresSold(data.total_litres_sold || 0);
                setTotalAmount(data.total_amount || 0);
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                console.error("Failed to fetch summary");
            }
        } catch (error) {
            console.error("Error fetching summary:", error);
        } finally {
            setSummaryLoading(false);
        }
    }, [searchTerm, filterDate, filterMonth, filterRoute, filterShift, token, navigate]);

    // Fetch routes on component mount
    useEffect(() => {
        fetchRoutes();
    }, [fetchRoutes]);

    // Fetch milk sales when dependencies change
    useEffect(() => {
        Promise.all([fetchMilkSales(), fetchSummary()]);
    }, [fetchMilkSales, fetchSummary]);

    const toggleDropdown = (index) => {
        if (isDropdownOpen === index) {
            setIsDropdownOpen(null);
        } else {
            setIsDropdownOpen(index);
        }
    };

    const handleDeleteClick = (milkSale) => {
        setSelectedMilkSale(milkSale);
        setIsConfirmDialogOpen(true);
    };

    const confirmDeleteMilkSale = async () => {
        if (selectedMilkSale) {
            try {
                const response = await fetch(`${API_URL}/milk-sales/${selectedMilkSale.id}`, {
                    method: 'DELETE',
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                });

                if (response.ok) {
                    setNotification({ type: "success", message: 'Milk sale deleted successfully' });
                    fetchMilkSales();
                    fetchSummary();
                } else if (response.status === 401) {
                    navigate("/logout");
                } else if (response.status === 404) {
                    setNotification({ type: "danger", message: 'Milk sale not found.' });
                } else {
                    setNotification({ type: "danger", message: 'There was an error.' });
                    throw new Error('Failed to delete milk sale');
                }
            } catch (error) {
                setNotification({ message: 'An error occurred while deleting the milk sale.', type: 'danger' });
                console.error("Error:", error);
            } finally {
                setIsConfirmDialogOpen(false);
                setSelectedMilkSale(null);
            }
        }
    };

    const cancelDeleteMilkSale = () => {
        setIsConfirmDialogOpen(false);
        setSelectedMilkSale(null);
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    const handleFilterDateChange = (event) => {
        setFilterDate(event.target.value);
        setCurrentPage(1);
    };

    const handleFilterMonthChange = (event) => {
        setFilterMonth(event.target.value);
        setCurrentPage(1);
    };

    const handleFilterRouteChange = (event) => {
        setFilterRoute(event.target.value);
        setCurrentPage(1);
    };

    const handleFilterShiftChange = (event) => {
        setFilterShift(event.target.value);
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setSearchTerm("");
        setFilterDate("");
        setFilterMonth("");
        setFilterRoute("");
        setFilterShift("");
        setCurrentPage(1);
    };

    // Handle click outside dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDropdownOpen !== null) {
                const dropdown = document.querySelector(`.dropdown.is-active`);
                if (dropdown && !dropdown.contains(event.target)) {
                    setIsDropdownOpen(null);
                }
            }
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [isDropdownOpen]);

    return (
        <div>
            <main>
                <nav className="level">
                    <div className="level-left">
                        <div className="level-item">
                            <span className="title">Milk Sales</span>
                        </div>
                    </div>

                    <div className="level-item has-text-centered">
                        <div>
                            <p>
                                Total Sold: <strong>
                                    {summaryLoading ? '...' : `${totalLitresSold} L`}
                                </strong> &nbsp;
                                Amount: {summaryLoading ? '...' : formatCurrency(totalAmount)}
                            </p>
                        </div>
                    </div>

                    <div className="level-right">
                        <p className="level-item">
                            <Link to="add" className="button primary-bg">
                                <i className="fa fa-plus" aria-hidden="true"></i> &nbsp; New Sale
                            </Link>
                        </p>
                    </div>
                </nav>


                {notification.message && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                {/* Filters */}
                <div className="field is-horizontal">
                    <div className="field-body">
                        {/* <div className="field">
                            <label className="label">Search</label>
                            <div className="control">
                                <input
                                    className="input"
                                    type="text"
                                    placeholder="Search by route or notes"
                                    value={searchTerm}
                                    onChange={handleSearch}
                                />
                            </div>
                        </div> */}
                        <div className="field">
                            <label className="label">Filter by Route</label>
                            <div className="control">
                                <div className="select is-fullwidth">
                                    <select
                                        value={filterRoute}
                                        onChange={handleFilterRouteChange}
                                    >
                                        <option value="">All Routes</option>
                                        {routes.map((route) => (
                                            <option key={route.id} value={route.id}>
                                                {route.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="field">
                            <label className="label">Filter by Shift</label>
                            <div className="control">
                                <div className="select is-fullwidth">
                                    <select
                                        value={filterShift}
                                        onChange={handleFilterShiftChange}
                                    >
                                        {shifts.map((shift) => (
                                            <option key={shift.value} value={shift.value}>
                                                {shift.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="field">
                            <label className="label">Filter by Date</label>
                            <div className="control">
                                <input
                                    type="date"
                                    className="input"
                                    value={filterDate}
                                    onChange={handleFilterDateChange}
                                />
                            </div>
                        </div>
                        <div className="field">
                            <label className="label">Filter by Month</label>
                            <div className="control">
                                <input
                                    type="month"
                                    className="input"
                                    value={filterMonth}
                                    onChange={handleFilterMonthChange}
                                />
                            </div>
                        </div>
                        <div className="field">
                            <label className="label">.</label>
                            <div className="control">
                                <button
                                    className="button is-light"
                                    onClick={clearFilters}
                                    disabled={!searchTerm && !filterDate && !filterMonth && !filterRoute && !filterShift}
                                >
                                    Clear Filters
                                </button>
                            </div>

                        </div>

                    </div>
                </div>

                {loading ? (
                    <p>Loading milk sales...</p>
                ) : error ? (
                    <p className="has-text-danger">{error}</p>
                ) : (
                    <>
                        <table className="table is-striped is-fullwidth">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Route</th>
                                    <th>Date & Shift</th>
                                    {isAdmin && (
                                        <th>Aggregated (L)</th>
                                    )}

                                    <th>Sold (L)</th>
                                    {isAdmin && (
                                        <th>Remaining (L)</th>
                                    )}

                                    <th>Price/L</th>
                                    <th>Total Amount</th>
                                    {/* <th>Sold By</th> */}
                                    <th>Posted At</th>
                                    <th>Options</th>
                                </tr>
                            </thead>
                            <tbody>
                                {milkSales.length > 0 ? (
                                    milkSales.map((sale, index) => {
                                        const aggregation = sale.route_aggregation;
                                        const remaining = aggregation ?
                                            (parseFloat(aggregation.litres) - parseFloat(sale.litres))?.toFixed(2) :
                                            'N/A';

                                        return (
                                            <tr key={sale.id}>
                                                <td>{(currentPage - 1) * perPage + index + 1}</td>
                                                <td>
                                                    {aggregation?.route?.name || 'N/A'}
                                                </td>
                                                <td>
                                                    {aggregation ? (
                                                        <>
                                                            {formatToDayMonthYear(aggregation.collection_date)}<br />
                                                            <small className="tag is-light">{aggregation.shift}</small>
                                                        </>
                                                    ) : 'N/A'}
                                                </td>
                                                {isAdmin && (
                                                    <td>{aggregation?.litres ? parseFloat(aggregation.litres).toFixed(2) : 'N/A'}</td>
                                                )}
                                                <td className="has-text-weight-bold">
                                                    {parseFloat(sale.litres).toFixed(2)}
                                                </td>
                                                {isAdmin && (
                                                    <td>
                                                        {remaining}
                                                    </td>
                                                )}
                                                <td>
                                                    {sale.price_per_litre ? formatCurrency(sale.price_per_litre) : 'N/A'}
                                                </td>
                                                <td className="has-text-weight-bold">
                                                    {sale.total_amount ? formatCurrency(sale.total_amount) : 'N/A'}
                                                </td>
                                                {/* <td>{sale.seller?.name || 'N/A'}</td> */}
                                                <td>{(formatTimestamp(sale.created_at) || " ")}</td>
                                                <td>
                                                    <div className={`dropdown is-right ${isDropdownOpen === index ? 'is-active' : ''}`}>
                                                        <div className="dropdown-trigger">
                                                            <button
                                                                className="button is-white"
                                                                aria-haspopup="true"
                                                                aria-controls="dropdown-menu"
                                                                onClick={() => toggleDropdown(index)}
                                                            >
                                                                <span className="icon">
                                                                    <i className="fas fa-ellipsis-v"></i>
                                                                </span>
                                                            </button>
                                                        </div>
                                                        <div className="dropdown-menu" id="dropdown-menu" role="menu">
                                                            <div className="dropdown-content">
                                                                {canEdit(userRole) && (
                                                                    <>
                                                                        <Link to={`${sale.id}/details`} className="dropdown-item">
                                                                            <span className="icon">
                                                                                <i className="fas fa-info-circle"></i>
                                                                            </span>
                                                                            <span>Details</span>
                                                                        </Link>
                                                                        <Link to={`${sale.id}/update`} className="dropdown-item">
                                                                            <span className="icon">
                                                                                <i className="fas fa-edit"></i>
                                                                            </span>
                                                                            <span>Edit</span>
                                                                        </Link>
                                                                        <Link
                                                                            className="dropdown-item"
                                                                            onClick={() => handleDeleteClick(sale)}
                                                                        >
                                                                            <span className="icon">
                                                                                <i className="fas fa-trash-alt"></i>
                                                                            </span>
                                                                            <span>Delete</span>
                                                                        </Link>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="11" className="has-text-centered">
                                            No milk sales found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {/* Pagination Controls */}
                        <nav className="pagination is-centered">
                            <button
                                className="pagination-previous"
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            <span className="pagination-page">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                className="pagination-next"
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                            >
                                Next
                            </button>
                        </nav>
                    </>
                )}

                {/* Confirmation Dialog */}
                {isConfirmDialogOpen && (
                    <div className="modal is-active">
                        <div className="modal-background" onClick={cancelDeleteMilkSale}></div>
                        <div className="modal-card">
                            <header className="modal-card-head">
                                <p className="modal-card-title">Confirm Delete</p>
                                <button className="delete" aria-label="close" onClick={cancelDeleteMilkSale}></button>
                            </header>
                            <section className="modal-card-body">
                                <p>Are you sure you want to delete the milk sale for route
                                    "<strong>{selectedMilkSale?.route_aggregation?.route?.name}</strong>" on
                                    {formatToDayMonthYear(selectedMilkSale?.route_aggregation?.collection_date)} ({selectedMilkSale?.route_aggregation?.shift})?
                                </p>
                                <p className="has-text-danger">This will make the sold milk available again for processing.</p>
                            </section>
                            <footer className="modal-card-foot">
                                <button className="button is-danger" onClick={confirmDeleteMilkSale}>
                                    Delete
                                </button>
                                <button className="button" onClick={cancelDeleteMilkSale}>
                                    Cancel
                                </button>
                            </footer>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}

export default MilkSales;